import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { formatToPriceType } from 'state/secondaryMarket/live/live.selector';
import type { AppState } from 'state/state.model';

function select(state: AppState, tabId: string) {
  return state.secondaryMarket.live.quotes[tabId];
}

export function getLiveQuoteXMarginPlusUpfront(state: AppState, tabId: string) {
  const salesMargin = getLiveQuoteSalesMargin(state, tabId) ?? 0;
  const upfront = getLiveQuoteUpfront(state, tabId) ?? 0;
  return formatToPriceType(state, tabId, salesMargin + upfront);
}

export function getLiveInitialUpfront(state: AppState, tabId: string) {
  const quoteUpfront = getLiveQuoteUpfront(state, tabId);
  return formatToPriceType(state, tabId, quoteUpfront);
}

export function getLiveQuoteStatus(state: AppState, tabId: string) {
  return select(state, tabId)?.quoteStatus;
}
export function isLiveQuoteExpired(state: AppState, tabId: string) {
  return getLiveQuoteStatus(state, tabId) === 'QUOTED_EXPIRED';
}
export function isLiveQuoteEnded(state: AppState, tabId: string) {
  return getLiveQuoteStatus(state, tabId) === 'QUOTED_ENDED';
}
export function getLiveQuotePrice(state: AppState, tabId: string) {
  return select(state, tabId)?.price;
}
export function getLiveQuoteQuotationType(state: AppState, tabId: string) {
  return select(state, tabId)?.quotationType;
}

export function getLiveQuoteSalesAdjustedCleanPriceFormatted(state: AppState, tabId: string) {
  const price = getLiveQuoteSalesAdjustedCleanPrice(state, tabId);
  return formatToPriceType(state, tabId, price);
}

export function getLiveQuotePriceType(state: AppState, tabId: string) {
  return select(state, tabId)?.priceType;
}

export function getLiveQuoteUpfront(state: AppState, tabId: string) {
  return select(state, tabId)?.upfront;
}

export function getLiveQuoteSalesMargin(state: AppState, tabId: string) {
  return select(state, tabId)?.salesMargin;
}

export function getLiveQuoteSalesMarginFormatted(state: AppState, tabId: string) {
  const salesMargin = getLiveQuoteSalesMargin(state, tabId);
  return formatToPriceType(state, tabId, salesMargin);
}

export function getLiveQuoteTradingPrice(state: AppState, tabId: string) {
  return select(state, tabId)?.tradingPrice;
}

export function getLiveQuoteFormattedAccruedInterest(state: AppState, tabId: string) {
  return formatToPriceType(state, tabId, select(state, tabId)?.accruedInterest);
}
export function getLiveQuoteTradingPriceFormatted(state: AppState, tabId: string) {
  const tradingPrice = getLiveQuoteTradingPrice(state, tabId);
  return formatToPriceType(state, tabId, tradingPrice);
}

export function getLiveQuoteQuoteId(state: AppState, tabId: string) {
  return select(state, tabId)?.quoteId;
}

export function getLiveQuoteTradeWay(state: AppState, tabId: string) {
  return select(state, tabId)?.tradeWay;
}

export function getLiveQuoteSettlementDate(state: AppState, tabId: string) {
  return select(state, tabId)?.settlementDate;
}

export function getLiveQuoteTradeAuthStartTimeInMillis(state: AppState, tabId: string) {
  return select(state, tabId)?.tradeAuthStartTimeInMillis;
}

export function getLiveQuoteErrorMessage(state: AppState, tabId: string) {
  return select(state, tabId)?.errorMessage;
}

export function getLiveQuoteCleanPrice(state: AppState, tabId: string) {
  return select(state, tabId)?.cleanPrice;
}

export function getLiveQuoteTimeout(state: AppState, tabId: string) {
  return select(state, tabId)?.timeout;
}
export function getLiveQuoteTradeAuthDurationInMillis(state: AppState, tabId: string) {
  return select(state, tabId)?.tradeAuthDurationInMillis;
}

export function getLiveQuoteQuantity(state: AppState, tabId: string) {
  return select(state, tabId)?.quantity;
}

export function getLiveQuoteEffectiveCashAmount(state: AppState, tabId: string) {
  return select(state, tabId)?.effectiveAmount;
}

function computeSalesAdjustedPrice(state: AppState, tabId: string, price?: number): number | undefined {
  const salesMarginAdj = liveFormInputSelectors.getFormInputCustomizedXMarginAdjustment(state, tabId) ?? 0;
  const tradingPriceAdj = liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustment(state, tabId) ?? 0;

  if (price !== undefined) {
    return salesMarginAdj + price + tradingPriceAdj;
  }
  return undefined;
}
export function getLiveQuoteSalesAdjustedCleanPrice(state: AppState, tabId: string): number | undefined {
  const cleanPrice = getLiveQuoteCleanPrice(state, tabId);
  return computeSalesAdjustedPrice(state, tabId, cleanPrice);
}

export function getLiveQuoteGreeks(state: AppState, tabId: string) {
  return select(state, tabId)?.greeks ?? [];
}
